import { mdsSidebarWidths } from "@/design-system/foundations";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import React, { useMemo, useCallback } from "react";
import styled from "@emotion/styled";
import { MdsHoverContent } from "@/design-system/components/hover-content/MdsHoverContent";
import { SidebarDropdownMenu } from "@/components/layout/components/sidebar/SidebarDropdownMenu";
import { SidebarContents } from "@/components/layout/components/sidebar/sidebar-contents";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon";
import {
  StyledAccountInfoWrapper,
  StyledSidebarWrapper,
} from "@/components/layout/components/sidebar/sidebar.styles";
import { SidebarAccountInfo } from "@/components/layout/components/sidebar/SidebarAccountInfo";
import { MdsHoverContentGroup } from "@/design-system/components/hover-content/MdsHoverContentGroup";

interface SidebarExpandedProps {
  setIsImportModalOpen: (b: boolean) => void;
  toggleSidebar: () => void;
  shouldHideSidebarToggle: boolean;
}

export const SidebarExpanded = observer(
  ({ setIsImportModalOpen, toggleSidebar, shouldHideSidebarToggle }: SidebarExpandedProps) => {
    const { store } = useAppStore();
    const { profileDisplayName, profilePhotoUrl } = store.account.myAccount;

    const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    const hoverGroup = useMemo(() => new MdsHoverContentGroup(), []);
    const closeAll = useCallback(() => hoverGroup.hideAll({}), [hoverGroup]);

    return (
      <StyledSidebarExpandedWrapper onClick={stopPropagation}>
        <MdsHoverContent
          group={hoverGroup}
          requireClick
          content={() => (
            <SidebarDropdownMenu
              displayAccountInfo
              setIsImportModalOpen={setIsImportModalOpen}
              onClick={closeAll}
            />
          )}
        >
          <StyledAccountInfoWrapper>
            <SidebarAccountInfo
              profileDisplayName={profileDisplayName}
              profilePhotoUrl={profilePhotoUrl}
            />
          </StyledAccountInfoWrapper>
        </MdsHoverContent>
        <SidebarContents />
        {!shouldHideSidebarToggle && (
          <StyledSidebarToggleButton
            size={MDSIconButtonSize.Medium}
            iconKind={MdsIconKind.ArrowLeftToLine}
            variant={MDSIconButtonVariant.OnChrome}
            onClick={() => toggleSidebar()}
          />
        )}
      </StyledSidebarExpandedWrapper>
    );
  }
);

const StyledSidebarExpandedWrapper = styled(StyledSidebarWrapper)`
  width: ${mdsSidebarWidths().expanded}px;
  height: 100%;
  flex: 1;
  min-height: 0;
`;
StyledSidebarExpandedWrapper.displayName = "StyledSidebarExpandedWrapper";

const StyledSidebarToggleButton = styled(MDSIconButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.button.navigation.default};
  border-radius: ${({ theme }) => theme.borderRadius.none};
  border-top-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.xlarge};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.navigation.hover};
  }
`;
StyledSidebarToggleButton.displayName = "StyledSidebarToggleButton";
